/** biome-ignore-all lint/correctness/useExhaustiveDependencies: apiUrlCreator changes on workspace switch */
import type { ClientGitStatus } from "@app/commands/versionControl/statuses";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import BranchUpdaterService from "@ext/git/actions/Branch/BranchUpdaterService/logic/BranchUpdaterService";
import { useCallback, useEffect, useRef, useState } from "react";
import { BranchCheckoutBlockedDialog } from "../components/BranchCheckoutBlockedDialog";
import { BranchCheckoutDialog } from "../components/BranchCheckoutDialog";
import { type BranchCheckoutChoice, useBranchCheckoutSettingsStore } from "./BranchCheckoutSettingsStore";
import { getBranchCheckoutDecision } from "./getBranchCheckoutDecision";

/** Both the branch panel and checkout from a link use the same device-local preference. */
export const useBranchCheckout = () => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const [pending, setPending] = useState<{ fromBranch: string; toBranch: string } | null>(null);
	const [blockedBranch, setBlockedBranch] = useState<string | null>(null);
	const resolveChoice = useRef<((choice: BranchCheckoutChoice | null) => void) | null>(null);
	const busy = useRef(false);
	const mounted = useRef(true);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
			resolveChoice.current?.(null);
		};
	}, []);

	const finishChoice = (choice: BranchCheckoutChoice | null) => {
		const resolve = resolveChoice.current;
		resolveChoice.current = null;
		if (!resolve) return;
		if (choice) useBranchCheckoutSettingsStore.getState().setChoice(choice);
		setPending(null);
		resolve(choice);
	};

	const checkoutBranch = useCallback(
		async (branch: string, choiceOverride?: BranchCheckoutChoice) => {
			if (busy.current) return null;
			busy.current = true;
			try {
				const response = await FetchService.fetch<ClientGitStatus[]>(
					apiUrlCreator.getVersionControlStatuses(false),
				);
				if (!response.ok || !mounted.current) return null;
				const localChanges = await response.json();
				if (!mounted.current) return null;

				let decision = getBranchCheckoutDecision(localChanges, useBranchCheckoutSettingsStore.getState());
				if (choiceOverride) {
					useBranchCheckoutSettingsStore.getState().setChoice(choiceOverride);
					decision = getBranchCheckoutDecision(localChanges, {
						mode: choiceOverride.mode,
						hideConfirmation: true,
					});
				} else if (decision.needsConfirmation) {
					const choice = await new Promise<BranchCheckoutChoice | null>((resolve) => {
						resolveChoice.current = resolve;
						setPending({ fromBranch: BranchUpdaterService.branch?.name ?? "", toBranch: branch });
					});
					if (!choice) return null;
					decision = getBranchCheckoutDecision(localChanges, {
						mode: choice.mode,
						hideConfirmation: true,
					});
				}

				if (decision.blocked) {
					setBlockedBranch(BranchUpdaterService.branch?.name ?? "");
					return null;
				}
				if (!mounted.current) return null;
				return await FetchService.fetch<string>(
					apiUrlCreator.getVersionControlCheckoutBranchUrl(branch, decision.mode === "transfer"),
				);
			} finally {
				busy.current = false;
			}
		},
		[apiUrlCreator],
	);

	return {
		checkoutBranch,
		checkoutDialog:
			blockedBranch !== null ? (
				<BranchCheckoutBlockedDialog branch={blockedBranch} onClose={() => setBlockedBranch(null)} />
			) : pending === null ? null : (
				<BranchCheckoutDialog
					fromBranch={pending.fromBranch}
					onCancel={() => finishChoice(null)}
					onConfirm={finishChoice}
					toBranch={pending.toBranch}
				/>
			),
	};
};
